import { escapeLikePattern } from '@/lib/sql/escape-like';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/lib/supabase/auth';
import { mode } from '@/lib/config/app-mode';
import { logAdminAction } from './log-admin-action';
import { useProfileStore } from '@/lib/state/profile-store';
import {
  mockAdminFeedback,
  mockAdminUsers,
  mockAdminMetrics,
  mockAdminMetricsV2,
  mockAdminActivityLog,
  mockAdminFeedbackNotes,
  mockAdminUserDetails,
  mockUserActivity,
} from './mock-data';
import type {
  AdminSubRole,
  AdminFeedbackItem,
  AdminUserItem,
  AdminDashboardMetrics,
  AdminDashboardMetricsV2,
  AdminActivityLogEntry,
  FeedbackDetailData,
  FeedbackStatus,
  FeedbackPriority,
  AdminFeedbackNote,
  AdminUserDetail,
  UserActivitySummary,
  UserActivityEvent,
} from './types';
import type { FeedbackCategory } from '@/lib/feedback/types';
import { insertNotification } from '@/lib/notifications/client';

/**
 * Check if the current user has admin role in mock/demo mode.
 * In demo mode there's no Supabase user, so we check the Zustand store's userRole.
 * This prevents non-admin demo users from accessing admin data.
 */
function isMockAdmin(): boolean {
  return useProfileStore.getState().userRole === 'admin';
}

/**
 * Fetch current user's admin sub-role from admin_roles table.
 * In mock/demo mode, verifies the user has admin role before returning mock data.
 */
export function useAdminRole() {
  const { user } = useAuth();

  return useQuery<AdminSubRole | null>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- user?.id is the stable discriminant
    queryKey: ['admin-role', user?.id],
    queryFn: async () => {
      if (mode.showSampleData) {
        // Only return admin role if user actually has admin role in Zustand store
        return isMockAdmin() ? 'super_admin' : null;
      }

      if (!user) return null;

      // maybeSingle: "this user holds no admin role" is the NORMAL case for
      // almost every user, not an error condition.
      const { data, error } = await supabase
        .from('admin_roles')
        .select('admin_role')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error || !data) return null;
      return data.admin_role as AdminSubRole;
    },
    enabled: !!user || mode.showSampleData,
  });
}

/**
 * Fetch all feedback for admin view.
 * In mock/demo mode, verifies admin role before returning data.
 */
export function useAdminFeedback(filters?: { category?: FeedbackCategory; status?: FeedbackStatus }) {
  return useQuery<AdminFeedbackItem[]>({
    queryKey: ['admin-feedback', filters?.category, filters?.status],
    queryFn: async () => {
      if (mode.showSampleData) {
        if (!isMockAdmin()) return [];
        let items = mockAdminFeedback;
        if (filters?.category) {
          items = items.filter((f) => f.category === filters.category);
        }
        if (filters?.status) {
          items = items.filter((f) => f.status === filters.status);
        }
        return items;
      }

      let query = supabase
        .from('feedback')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters?.category) {
        query = query.eq('category', filters.category);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Fetch user emails separately (feedback.user_id → auth.users, not profiles, so PostgREST can't join)
      const rows = data || [];
      const userIds = [...new Set(rows.map((r) => r.user_id))];
      let emailMap: Record<string, string> = {};
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, email')
          .in('id', userIds);
        for (const p of profiles || []) {
          emailMap[p.id] = p.email;
        }
      }

      return rows.map((row) => ({
        id: row.id,
        user_id: row.user_id,
        user_email: emailMap[row.user_id] ?? 'unknown',
        platform: row.platform,
        category: row.category as FeedbackCategory,
        text: row.text,
        screenshot_path: row.screenshot_path,
        screen_context: row.screen_context,
        app_version: row.app_version,
        device_info: (row.device_info ?? {}) as Record<string, unknown>,
        created_at: row.created_at,
        status: (row.status ?? 'new') as FeedbackStatus,
        priority: (row.priority ?? 'medium') as FeedbackPriority,
        assigned_to: row.assigned_to,
        resolved_at: row.resolved_at,
        updated_at: row.updated_at ?? row.created_at,
      })) as AdminFeedbackItem[];
    },
  });
}

/**
 * Fetch all user profiles for admin view.
 * In mock/demo mode, verifies admin role before returning data.
 */
export function useAdminUsers(filters?: { role?: 'startup' | 'investor'; search?: string }) {
  return useQuery<AdminUserItem[]>({
    queryKey: ['admin-users', filters?.role, filters?.search],
    queryFn: async () => {
      if (mode.showSampleData) {
        if (!isMockAdmin()) return [];
        let items = mockAdminUsers;
        if (filters?.role) {
          items = items.filter((u) => u.role === filters.role);
        }
        if (filters?.search) {
          const q = filters.search.toLowerCase();
          items = items.filter((u) => u.email.toLowerCase().includes(q));
        }
        return items;
      }

      let query = supabase
        .from('profiles')
        .select('id, email, role, onboarding_completed, created_at')
        .order('created_at', { ascending: false });

      if (filters?.role) {
        query = query.eq('role', filters.role);
      }

      if (filters?.search) {
        query = query.ilike('email', `%${escapeLikePattern(filters.search)}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data || []) as AdminUserItem[];
    },
  });
}

/**
 * Fetch aggregate dashboard metrics (legacy).
 * In mock/demo mode, verifies admin role before returning data.
 */
export function useAdminMetrics() {
  return useQuery<AdminDashboardMetrics>({
    queryKey: ['admin-metrics'],
    queryFn: async () => {
      if (mode.showSampleData) {
        if (!isMockAdmin()) return { totalUsers: 0, totalStartups: 0, totalInvestors: 0, totalAdmins: 0, totalFeedback: 0, feedbackByCategory: { bug: 0, feature: 0, ux: 0, other: 0 }, recentSignups7d: 0 };
        return mockAdminMetrics;
      }

      const [profilesRes, feedbackRes, recentRes, adminRolesRes] = await Promise.all([
        supabase.from('profiles').select('role', { count: 'exact', head: false }),
        supabase.from('feedback').select('category', { count: 'exact', head: false }),
        supabase
          .from('profiles')
          .select('id', { count: 'exact', head: true })
          .gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()),
        // Authority, not persona — see the totalAdmins note below.
        supabase.from('admin_roles').select('user_id', { count: 'exact', head: true }),
      ]);

      const profiles: Array<{ role: string }> = profilesRes.data || [];
      const feedback: Array<{ category: string }> = feedbackRes.data || [];

      const totalStartups = profiles.filter((p) => p.role === 'startup').length;
      const totalInvestors = profiles.filter((p) => p.role === 'investor').length;
      // Administrators are counted from `admin_roles` (authority), not from the
      // persona column, which is never 'admin' under the single model — that
      // filter would report 0 administrators forever.
      const totalAdmins = adminRolesRes.count ?? 0;

      const feedbackByCategory = {
        bug: feedback.filter((f) => f.category === 'bug').length,
        feature: feedback.filter((f) => f.category === 'feature').length,
        ux: feedback.filter((f) => f.category === 'ux').length,
        other: feedback.filter((f) => f.category === 'other').length,
      };

      return {
        totalUsers: profiles.length,
        totalStartups,
        totalInvestors,
        totalAdmins,
        totalFeedback: feedback.length,
        feedbackByCategory,
        recentSignups7d: recentRes.count ?? 0,
      } satisfies AdminDashboardMetrics;
    },
  });
}

// ============================================================
// New hooks for V2 admin system
// ============================================================

/**
 * Fetch extended dashboard metrics with growth, engagement, and feedback status.
 * In mock/demo mode, verifies admin role before returning data.
 */
export function useAdminMetricsV2() {
  return useQuery<AdminDashboardMetricsV2>({
    queryKey: ['admin-metrics-v2'],
    queryFn: async () => {
      if (mode.showSampleData) {
        if (!isMockAdmin()) return { totalUsers: 0, totalStartups: 0, totalInvestors: 0, totalAdmins: 0, totalFeedback: 0, feedbackByCategory: { bug: 0, feature: 0, ux: 0, other: 0 }, recentSignups7d: 0, usersDelta: 0, startupsDelta: 0, investorsDelta: 0, feedbackDelta: 0, dau: 0, wau: 0, mau: 0, openFeedbackCount: 0, criticalBugs: 0, feedbackByStatus: { new: 0, in_review: 0, resolved: 0, closed: 0 }, avgScore: 0, messageVolume: 0, dataRoomActivity: 0, pendingOnboarding: 0, signupTrend: [] };
        return mockAdminMetricsV2;
      }

      // Fetch KPIs from analytics view + feedback stats + pending onboarding in parallel
      const [kpisRes, feedbackRes, recentSignupsRes, pendingOnboardingRes] = await Promise.all([
        supabase.from('v_executive_kpis').select('*').single(),
        supabase.from('feedback').select('category, status', { count: 'exact', head: false }),
        supabase
          .from('profiles')
          .select('id, created_at')
          .gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())
          .order('created_at', { ascending: true }),
        supabase
          .from('profiles')
          .select('id', { count: 'exact', head: true })
          .eq('onboarding_completed', false)
          .neq('role', 'admin'),
      ]);

      const kpis = kpisRes.data;
      const feedback: Array<{ category: string; status: string }> = feedbackRes.data || [];
      const recentSignups: Array<{ id: string; created_at: string | null }> = recentSignupsRes.data || [];
      const pendingOnboarding = pendingOnboardingRes.count ?? 0;

      // Build 7-day signup trend
      const signupTrend: { date: string; count: number }[] = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
        const dateStr = d.toISOString().split('T')[0];
        signupTrend.push({
          date: dateStr,
          count: recentSignups.filter((s) => s.created_at?.startsWith(dateStr)).length,
        });
      }

      const totalAdmins = kpis
        ? (kpis.total_users ?? 0) - (kpis.total_startups ?? 0) - (kpis.total_investors ?? 0)
        : 0;

      return {
        totalUsers: kpis?.total_users ?? 0,
        totalStartups: kpis?.total_startups ?? 0,
        totalInvestors: kpis?.total_investors ?? 0,
        totalAdmins,
        totalFeedback: feedback.length,
        feedbackByCategory: {
          bug: feedback.filter((f) => f.category === 'bug').length,
          feature: feedback.filter((f) => f.category === 'feature').length,
          ux: feedback.filter((f) => f.category === 'ux').length,
          other: feedback.filter((f) => f.category === 'other').length,
        },
        recentSignups7d: kpis?.signups_7d ?? recentSignups.length,
        usersDelta: 0,
        startupsDelta: 0,
        investorsDelta: 0,
        feedbackDelta: 0,
        dau: kpis?.dau ?? 0,
        wau: kpis?.wau ?? 0,
        mau: kpis?.mau ?? 0,
        openFeedbackCount: feedback.filter((f) => f.status === 'new' || f.status === 'in_review').length,
        criticalBugs: feedback.filter((f) => f.category === 'bug' && f.status !== 'resolved' && f.status !== 'closed').length,
        feedbackByStatus: {
          new: feedback.filter((f) => f.status === 'new').length,
          in_review: feedback.filter((f) => f.status === 'in_review').length,
          resolved: feedback.filter((f) => f.status === 'resolved').length,
          closed: feedback.filter((f) => f.status === 'closed').length,
        },
        avgScore: kpis?.avg_inverio_score ?? 0,
        messageVolume: kpis?.messages_7d ?? 0,
        dataRoomActivity: kpis?.data_room_activity_7d ?? 0,
        pendingOnboarding,
        signupTrend,
      } satisfies AdminDashboardMetricsV2;
    },
  });
}

/**
 * Fetch admin activity log.
 * In mock/demo mode, verifies admin role before returning data.
 */
export function useAdminActivityLog(limit = 10) {
  return useQuery<AdminActivityLogEntry[]>({
    queryKey: ['admin-activity-log', limit],
    queryFn: async () => {
      if (mode.showSampleData) {
        if (!isMockAdmin()) return [];
        return mockAdminActivityLog.slice(0, limit);
      }

      const { data, error } = await supabase
        .from('admin_activity_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      // Fetch admin emails separately (admin_user_id refs auth.users, not profiles)
      const rows = data || [];
      // admin_user_id is NULL once that admin erased their account: the entry
      // is retained, anonymized (FK ON DELETE SET NULL, 20261015120100).
      const adminIds = [
        ...new Set(rows.map((r) => r.admin_user_id).filter((id): id is string => id !== null)),
      ];
      let emailMap: Record<string, string> = {};
      if (adminIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, email')
          .in('id', adminIds);
        for (const p of profiles || []) {
          emailMap[p.id] = p.email;
        }
      }

      return rows.map((row) => ({
        id: row.id,
        admin_email:
          row.admin_user_id === null
            ? 'Deleted admin'
            : emailMap[row.admin_user_id] ?? 'unknown',
        action: row.action,
        target_type: row.target_type,
        target_id: row.target_id,
        details: row.details ?? {},
        created_at: row.created_at,
      })) as AdminActivityLogEntry[];
    },
  });
}

/**
 * Fetch single feedback item with notes.
 * In mock/demo mode, verifies admin role before returning data.
 */
export function useAdminFeedbackDetail(feedbackId: string | undefined) {
  return useQuery<FeedbackDetailData | null>({
    queryKey: ['admin-feedback-detail', feedbackId],
    queryFn: async () => {
      if (!feedbackId) return null;

      if (mode.showSampleData) {
        if (!isMockAdmin()) return null;
        const feedback = mockAdminFeedback.find((f) => f.id === feedbackId);
        if (!feedback) return null;
        const notes = mockAdminFeedbackNotes[feedbackId] ?? [];
        return { feedback, notes };
      }

      // Fetch feedback + notes in parallel (no FK joins — user_id refs auth.users, not profiles)
      const [fbRes, notesRes] = await Promise.all([
        supabase
          .from('feedback')
          .select('*')
          .eq('id', feedbackId)
          .single(),
        supabase
          .from('admin_feedback_notes')
          .select('*')
          .eq('feedback_id', feedbackId)
          .order('created_at', { ascending: true }),
      ]);

      if (fbRes.error) throw fbRes.error;

      const row = fbRes.data;

      // Fetch user emails for feedback submitter + note authors
      const allUserIds = [
        row.user_id,
        ...(notesRes.data || []).map((n) => n.admin_user_id),
      ].filter(Boolean);
      const uniqueIds = [...new Set(allUserIds)];
      let emailMap: Record<string, string> = {};
      if (uniqueIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, email')
          .in('id', uniqueIds);
        for (const p of profiles || []) {
          emailMap[p.id] = p.email;
        }
      }

      const feedback: AdminFeedbackItem = {
        id: row.id,
        user_id: row.user_id,
        user_email: emailMap[row.user_id] ?? 'unknown',
        platform: row.platform,
        category: row.category as FeedbackCategory,
        text: row.text,
        screenshot_path: row.screenshot_path,
        screen_context: row.screen_context,
        app_version: row.app_version,
        device_info: (row.device_info ?? {}) as Record<string, unknown>,
        created_at: row.created_at,
        status: (row.status ?? 'new') as FeedbackStatus,
        priority: (row.priority ?? 'medium') as FeedbackPriority,
        assigned_to: row.assigned_to,
        resolved_at: row.resolved_at,
        updated_at: row.updated_at ?? row.created_at,
      };

      const notes: AdminFeedbackNote[] = (notesRes.data || []).map((n) => ({
        id: n.id,
        feedback_id: n.feedback_id,
        admin_email: emailMap[n.admin_user_id] ?? 'unknown',
        text: n.text,
        created_at: n.created_at,
      }));

      return { feedback, notes };
    },
    enabled: !!feedbackId,
  });
}

/**
 * Mutation to update feedback status.
 * In mock/demo mode, verifies admin role before allowing mutation.
 */
export function useUpdateFeedbackStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ feedbackId, status }: { feedbackId: string; status: FeedbackStatus }) => {
      if (mode.showSampleData) {
        if (!isMockAdmin()) throw new Error('Unauthorized: admin role required');
        // Update local mock data
        const item = mockAdminFeedback.find((f) => f.id === feedbackId);
        if (item) {
          item.status = status;
          item.updated_at = new Date().toISOString();
          if (status === 'resolved') item.resolved_at = new Date().toISOString();
        }
        return;
      }

      const updates: Record<string, unknown> = { status };
      if (status === 'resolved') updates.resolved_at = new Date().toISOString();

      const { error } = await supabase
        .from('feedback')
        .update(updates)
        .eq('id', feedbackId);

      if (error) throw error;

      // Notify the feedback submitter about the status change
      const { data: fb } = await supabase
        .from('feedback')
        .select('user_id')
        .eq('id', feedbackId)
        .maybeSingle();

      if (fb?.user_id) {
        insertNotification({
          userId: fb.user_id,
          type: 'feedback_update',
          title: 'Feedback status updated',
          body: `Your feedback has been marked as ${status.replace('_', ' ')}.`,
          data: { feedbackId, status },
        });
      }
    },
    onSuccess: (_data, { feedbackId, status }) => {
      // The feed renders 'feedback_resolved' with its own icon and label, so a
      // resolution is recorded as that rather than as a generic transition.
      void logAdminAction({
        action: status === 'resolved' ? 'feedback_resolved' : 'feedback_status_changed',
        targetType: 'feedback',
        targetId: feedbackId,
        details: { to: status },
      });
      queryClient.invalidateQueries({ queryKey: ['admin-feedback'] });
      queryClient.invalidateQueries({ queryKey: ['admin-feedback-detail', feedbackId] });
      queryClient.invalidateQueries({ queryKey: ['admin-metrics-v2'] });
    },
  });
}

/**
 * Mutation to update feedback priority.
 * In mock/demo mode, verifies admin role before allowing mutation.
 */
export function useUpdateFeedbackPriority() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ feedbackId, priority }: { feedbackId: string; priority: FeedbackPriority }) => {
      if (mode.showSampleData) {
        if (!isMockAdmin()) throw new Error('Unauthorized: admin role required');
        const item = mockAdminFeedback.find((f) => f.id === feedbackId);
        if (item) {
          item.priority = priority;
          item.updated_at = new Date().toISOString();
        }
        return;
      }

      const { error } = await supabase
        .from('feedback')
        .update({ priority })
        .eq('id', feedbackId);

      if (error) throw error;
    },
    onSuccess: (_data, { feedbackId, priority }) => {
      void logAdminAction({
        action: 'feedback_priority_changed',
        targetType: 'feedback',
        targetId: feedbackId,
        details: { to: priority },
      });
      queryClient.invalidateQueries({ queryKey: ['admin-feedback'] });
      queryClient.invalidateQueries({ queryKey: ['admin-feedback-detail', feedbackId] });
    },
  });
}

/**
 * Mutation to add a note to a feedback item.
 * In mock/demo mode, verifies admin role before allowing mutation.
 */
export function useAddFeedbackNote() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ feedbackId, text }: { feedbackId: string; text: string }) => {
      if (mode.showSampleData) {
        if (!isMockAdmin()) throw new Error('Unauthorized: admin role required');
        const note: AdminFeedbackNote = {
          id: `note-${Date.now()}`,
          feedback_id: feedbackId,
          admin_email: 'admin@inverio.ai',
          text,
          created_at: new Date().toISOString(),
        };
        if (!mockAdminFeedbackNotes[feedbackId]) {
          mockAdminFeedbackNotes[feedbackId] = [];
        }
        mockAdminFeedbackNotes[feedbackId].push(note);
        return note;
      }

      const { data, error } = await supabase
        .from('admin_feedback_notes')
        .insert({
          feedback_id: feedbackId,
          admin_user_id: user!.id,
          text,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_data, { feedbackId }) => {
      void logAdminAction({
        action: 'note_added',
        targetType: 'feedback',
        targetId: feedbackId,
        // The note TEXT is deliberately not logged: the feed is readable by
        // every super_admin and internal notes can quote a user's report.
      });
      queryClient.invalidateQueries({ queryKey: ['admin-feedback-detail', feedbackId] });
    },
  });
}

/**
 * Fetch detailed user profile for admin.
 * In mock/demo mode, verifies admin role before returning data.
 */
export function useAdminUserDetail(userId: string | undefined) {
  return useQuery<AdminUserDetail | null>({
    queryKey: ['admin-user-detail', userId],
    queryFn: async () => {
      if (!userId) return null;

      if (mode.showSampleData) {
        if (!isMockAdmin()) return null;
        return mockAdminUserDetails[userId] ?? null;
      }

      // Fetch profile + all counts in parallel
      const [
        profileRes,
        feedbackCountRes,
        sessionRes,
        messageCountRes,
        scoreRes,
        dataRoomFilesRes,
        dataRoomViewsRes,
      ] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, email, role, onboarding_completed, created_at')
          .eq('id', userId)
          .single(),
        supabase
          .from('feedback')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId),
        // last_active + login_count from user_sessions
        supabase
          .from('user_sessions')
          .select('started_at')
          .eq('user_id', userId)
          .order('started_at', { ascending: false }),
        // message_count: rows in dd_conversation_messages where investor_user_id = userId
        supabase
          .from('dd_conversation_messages')
          .select('id', { count: 'exact', head: true })
          .eq('investor_user_id', userId),
        // latest overall_score for this user's startup. score_results was dropped
        // in the 116.x scoring cutover; the canonical headline score now lives in
        // the v_mcp_startup_score_summary view (over score_event).
        supabase
          .from('v_mcp_startup_score_summary')
          .select('overall_score')
          .eq('startup_id', userId)
          .order('scored_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        // data_room_files: distinct documents this user has accessed
        supabase
          .from('document_access_logs')
          .select('document_id')
          .eq('user_id', userId),
        // data_room_views: access_type = 'view' for this user
        supabase
          .from('document_access_logs')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('access_type', 'view'),
      ]);

      if (profileRes.error) throw profileRes.error;
      const p = profileRes.data;

      const sessions = sessionRes.data ?? [];
      const last_active = sessions.length > 0 ? sessions[0].started_at : null;
      const login_count = sessions.length;

      // Deduplicate document_ids for data_room_files
      const accessedDocIds = dataRoomFilesRes.data ?? [];
      const uniqueDocIds = new Set(accessedDocIds.map((r) => r.document_id));
      const data_room_files = uniqueDocIds.size;

      return {
        id: p.id,
        email: p.email,
        role: p.role,
        onboarding_completed: p.onboarding_completed ?? false,
        created_at: p.created_at ?? '',
        last_active,
        login_count,
        feedback_count: feedbackCountRes.count ?? 0,
        message_count: messageCountRes.count ?? 0,
        score: scoreRes.data?.overall_score ?? null,
        data_room_files,
        data_room_views: dataRoomViewsRes.count ?? 0,
      } satisfies AdminUserDetail;
    },
    enabled: !!userId,
  });
}

/**
 * Fetch user activity summary.
 * In mock/demo mode, verifies admin role before returning data.
 */
export function useAdminUserActivity(userId: string | undefined) {
  return useQuery<UserActivitySummary | null>({
    queryKey: ['admin-user-activity', userId],
    queryFn: async () => {
      if (!userId) return null;

      if (mode.showSampleData) {
        if (!isMockAdmin()) return null;
        return mockUserActivity[userId] ?? { activityByDay: [], recentEvents: [] };
      }

      // Query last 14 days of events + last 10 events for this user in parallel
      const fourteenDaysAgo = new Date();
      fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
      const isoStart = fourteenDaysAgo.toISOString();

      const [histogramRes, recentRes] = await Promise.all([
        supabase
          .from('analytics_events')
          .select('created_at')
          .eq('user_id', userId)
          .gte('created_at', isoStart),
        supabase
          .from('analytics_events')
          .select('id, event_name, properties, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(10),
      ]);

      // Build 14-day histogram via client-side grouping
      const countsByDate: Record<string, number> = {};
      for (const row of histogramRes.data ?? []) {
        const d = new Date(row.created_at);
        const label = `${d.getMonth() + 1}/${String(d.getDate()).padStart(2, '0')}`;
        countsByDate[label] = (countsByDate[label] ?? 0) + 1;
      }
      const activityByDay = Object.entries(countsByDate)
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => {
          // Sort chronologically — parse MM/DD relative to today's year
          const toMs = (mmdd: string) => {
            const [mm, dd] = mmdd.split('/').map(Number);
            const now = new Date();
            const candidate = new Date(now.getFullYear(), mm - 1, dd);
            if (candidate > now) candidate.setFullYear(candidate.getFullYear() - 1);
            return candidate.getTime();
          };
          return toMs(a.date) - toMs(b.date);
        });

      // Map event_name string to the typed event_type union
      function mapEventType(eventName: string): UserActivityEvent['event_type'] {
        if (eventName === 'sign_in' || eventName === 'session_start') return 'login';
        if (eventName.startsWith('feedback_')) return 'feedback';
        if (eventName.startsWith('dd_message_') || eventName.startsWith('chat_')) return 'message';
        if (eventName.startsWith('document_upload') || eventName.startsWith('file_')) return 'upload';
        if (eventName.startsWith('score_')) return 'score_update';
        if (eventName.startsWith('onboarding_')) return 'onboarding';
        if (eventName.startsWith('data_room_') || eventName.startsWith('document_view')) return 'data_room_view';
        return 'login';
      }

      function humanize(eventName: string): string {
        return eventName
          .replace(/_/g, ' ')
          .replace(/^\w/, (c) => c.toUpperCase());
      }

      const recentEvents: UserActivityEvent[] = (recentRes.data ?? []).map((row) => ({
        id: row.id,
        event_type: mapEventType(row.event_name),
        description: humanize(row.event_name),
        metadata: (row.properties as Record<string, unknown>) ?? {},
        timestamp: row.created_at,
      }));

      return { activityByDay, recentEvents };
    },
    enabled: !!userId,
  });
}
